/**
 * /settings/integrations — Admin hub for third-party service credentials.
 *
 * Cards are grouped by category. Each card loads its own status from
 * GET /api/admin/secrets and writes back via POST /api/admin/secrets.
 * Fields that are already set show a masked placeholder until the user
 * explicitly toggles "Update key".
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { InputAdornment } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SyncIcon from "@mui/icons-material/Sync";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentIcon from "@mui/icons-material/Payment";
import EmailIcon from "@mui/icons-material/Email";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import WorkIcon from "@mui/icons-material/Work";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import { api, errorMessage } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { implementationInstalledQueryOptions } from "@/lib/queries";

export const Route = createFileRoute("/_auth/settings/integrations")({
  component: IntegrationsPage,
});

// ── Types ──────────────────────────────────────────────────────────────────

interface SecretInfo {
  key_name: string;
  is_set: boolean;
  label: string;
  description: string;
  last_4?: string | null;
}

interface AvailablePack {
  code: string;
  name: string;
  country: "US" | "CA";
  year: number;
  size_kb: number;
  is_installed: boolean;
  download_url: string;
}

interface DownloadPacksResult {
  downloaded: string[];
  failed: string[];
  message: string;
}

interface OperationsConfigField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  placeholder: string;
  help_text: string;
}

interface OperationsIntegration {
  provider: string;
  name: string;
  category: string;
  connection_mode: string;
  description: string;
  capabilities: string[];
  fields: OperationsConfigField[];
  docs_url: string;
  access_note: string;
  requires_partner_access: boolean;
  status: "not_configured" | "awaiting_provider_access" | "configured" | "connected" | "error" | "revoked";
  is_close_critical: boolean;
  visible_config: Record<string, string>;
  configured_fields: string[];
  last_tested_at?: string | null;
  last_success_at?: string | null;
  last_sync_at?: string | null;
  last_error?: string | null;
}

interface OperationsConnectionTest {
  ok: boolean;
  status: string;
  detail: string;
  latency_ms: number;
  tested_at: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const MASKED = "••••••••••••••••";

/** Returns a green "Connected" or grey "Not configured" chip. */
function StatusChip({ isSet }: { isSet: boolean }) {
  return isSet ? (
    <Chip label="Connected" color="success" size="small" />
  ) : (
    <Chip label="Not configured" size="small" />
  );
}

/** A password-type text field that shows a masked placeholder when the
 *  secret is already saved and the user hasn't toggled "Update". */
/** Approximate expected lengths for known secret prefixes — used to warn
 * the user if a paste looks suspiciously short. Anthropic keys are
 * ~108 chars; trailing whitespace from clipboard managers is common. */
const SECRET_SHAPE_HINTS: { prefix: string; min: number; label: string }[] = [
  { prefix: "sk-ant-api03-", min: 100, label: "Anthropic key" },
  { prefix: "sk-ant-",      min: 100, label: "Anthropic key" },
  { prefix: "sk-proj-",     min: 100, label: "OpenAI project key" },
  { prefix: "sk-",          min: 40,  label: "OpenAI key" },
];

function describeShape(value: string): { ok: boolean; hint: string } {
  if (!value) return { ok: false, hint: "" };
  const match = SECRET_SHAPE_HINTS.find((s) => value.startsWith(s.prefix));
  if (!match) {
    // Unknown shape — just report length so the user can eyeball it
    return { ok: value.length >= 16, hint: `${value.length} chars` };
  }
  const ok = value.length >= match.min;
  return {
    ok,
    hint: ok
      ? `${match.label}, ${value.length} chars ✓`
      : `${match.label} — looks short (${value.length} chars, expected ≥ ${match.min}). Re-paste?`,
  };
}

function SecretField({
  label,
  value,
  onChange,
  isSet,
  updating,
  onToggleUpdate,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  isSet: boolean;
  updating: boolean;
  onToggleUpdate: () => void;
  placeholder?: string;
}) {
  const [show, setShow] = useState(false);
  const isReadOnly = isSet && !updating;
  // Show shape feedback ONLY while editing — never while displaying a saved key
  const shape = !isReadOnly ? describeShape(value) : null;

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <Box sx={{ flex: 1 }}>
        <TextField
          label={label}
          type={!isReadOnly && show ? "text" : "password"}
          size="small"
          fullWidth
          value={isReadOnly ? MASKED : value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isReadOnly}
          placeholder={placeholder}
          // Auto-trim trailing whitespace on paste (most common cut-off cause —
          // clipboard managers + Anthropic console copy buttons frequently
          // append a \n or space which then trips backend min_length=4 logic
          // on the hash side and tools like httpx reject the header).
          onPaste={(e) => {
            if (isReadOnly) return;
            const pasted = e.clipboardData.getData("text");
            const cleaned = pasted.trim();
            if (pasted !== cleaned) {
              e.preventDefault();
              onChange(cleaned);
            }
            // else let the default paste handler run
          }}
          InputProps={{
            readOnly: isReadOnly,
            endAdornment: !isReadOnly ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShow((s) => !s)}
                  edge="end"
                  aria-label={show ? "Hide value" : "Show value"}
                >
                  {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        {/* Live shape hint — confirms the paste landed in full */}
        {shape && shape.hint && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              color: shape.ok ? "success.main" : "warning.main",
              fontFamily: "monospace",
              fontSize: 11,
            }}
          >
            {shape.hint}
          </Typography>
        )}
      </Box>
      {isSet && (
        <Button
          size="small"
          variant="outlined"
          onClick={onToggleUpdate}
          sx={{ whiteSpace: "nowrap", flexShrink: 0, height: 40 }}
        >
          {updating ? "Cancel" : "Update key"}
        </Button>
      )}
    </Box>
  );
}

// ── TaxJar card ────────────────────────────────────────────────────────────

const TAXJAR_COUNTRIES = ["US", "CA", "GB", "AU"] as const;

function TaxJarCard({ secrets }: { secrets: SecretInfo[] }) {
  const qc = useQueryClient();
  const secretInfo = secrets.find((s) => s.key_name === "taxjar_api_key");
  const isSet = secretInfo?.is_set ?? false;

  const [apiKey, setApiKey] = useState("");
  const [updating, setUpdating] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [saveResult, setSaveResult] = useState<string | null>(null);

  // Sync section
  const [syncCountries, setSyncCountries] = useState<Set<string>>(new Set(["US"]));
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [syncResult, setSyncResult] = useState<{
    regions_fetched: number;
    errors: string[];
  } | null>(null);

  const testMut = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; message: string }>("/api/admin/secrets/taxjar_api_key/test", {
        method: "POST",
      }),
    onSuccess: (r) => setTestResult(r),
    onError: (e) => setTestResult({ ok: false, message: errorMessage(e) }),
  });

  const saveMut = useMutation({
    mutationFn: (value: string) =>
      api("/api/admin/secrets", {
        method: "POST",
        body: { key_name: "taxjar_api_key", value },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-secrets"] });
      setSaveResult("API key saved.");
      setUpdating(false);
      setApiKey("");
    },
    onError: (e) => {
      console.error(e);
      setSaveResult(`Save failed — ${errorMessage(e)}`);
    },
  });

  const syncMut = useMutation({
    mutationFn: (params: { country_codes: string[]; effective_from?: string }) =>
      api<{ regions_fetched: number; errors: string[] }>("/api/tax/import/taxjar", {
        method: "POST",
        body: params,
      }),
    onSuccess: (r) => setSyncResult(r),
    onError: (e) => setSyncResult({ regions_fetched: 0, errors: [errorMessage(e)] }),
  });

  function toggleCountry(cc: string) {
    setSyncCountries((prev) => {
      const next = new Set(prev);
      if (next.has(cc)) next.delete(cc);
      else next.add(cc);
      return next;
    });
  }

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<ReceiptLongIcon color="primary" />}
        title="TaxJar"
        subheader="Sales tax calculation & filing"
        action={<StatusChip isSet={isSet} />}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* API key */}
        <SecretField
          label="API Key"
          value={apiKey}
          onChange={setApiKey}
          isSet={isSet}
          updating={updating}
          onToggleUpdate={() => {
            setUpdating((v) => !v);
            setApiKey("");
            setSaveResult(null);
          }}
        />

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={testMut.isPending || (!isSet && !apiKey)}
            startIcon={testMut.isPending ? <CircularProgress size={14} /> : undefined}
            onClick={() => {
              setTestResult(null);
              testMut.mutate();
            }}
          >
            Test Connection
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={saveMut.isPending || (!updating && isSet) || (!isSet && !apiKey)}
            onClick={() => saveMut.mutate(apiKey)}
          >
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </Box>

        {testResult && (
          <Alert severity={testResult.ok ? "success" : "error"} onClose={() => setTestResult(null)}>
            {testResult.message}
          </Alert>
        )}
        {saveResult && (
          <Alert severity={saveMut.isError ? "error" : "success"} onClose={() => setSaveResult(null)}>
            {saveResult}
          </Alert>
        )}

        <Divider />

        {/* Sync section */}
        <Typography variant="subtitle2" fontWeight={600}>
          Sync Tax Rates Now
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0 }}>
          {TAXJAR_COUNTRIES.map((cc) => (
            <FormControlLabel
              key={cc}
              control={
                <Checkbox
                  size="small"
                  checked={syncCountries.has(cc)}
                  onChange={() => toggleCountry(cc)}
                />
              }
              label={cc}
            />
          ))}
        </Box>
        <TextField
          label="Effective From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.target.value)}
          sx={{ maxWidth: 200 }}
        />
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              syncMut.isPending ? <CircularProgress size={14} /> : <SyncIcon />
            }
            disabled={syncMut.isPending || syncCountries.size === 0 || !isSet}
            onClick={() => {
              setSyncResult(null);
              syncMut.mutate({
                country_codes: Array.from(syncCountries),
                effective_from: effectiveFrom || undefined,
              });
            }}
          >
            {syncMut.isPending ? "Syncing…" : "Run Sync"}
          </Button>
          {!isSet && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Save API key first
            </Typography>
          )}
        </Box>
        {syncResult && (
          <Alert
            severity={syncResult.errors.length > 0 ? "warning" : "success"}
            onClose={() => setSyncResult(null)}
          >
            <Typography variant="body2" fontWeight={600}>
              Sync complete — {syncResult.regions_fetched} regions fetched
            </Typography>
            {syncResult.errors.map((e, i) => (
              <Typography key={i} variant="caption" display="block" color="error.main">
                {e}
              </Typography>
            ))}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ── Stripe card ────────────────────────────────────────────────────────────

function StripeCard({ secrets }: { secrets: SecretInfo[] }) {
  const qc = useQueryClient();
  const secretInfo = secrets.find((s) => s.key_name === "stripe_webhook_secret");
  const isSet = secretInfo?.is_set ?? false;

  const [secret, setSecret] = useState("");
  const [updating, setUpdating] = useState(false);
  const [saveResult, setSaveResult] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const webhookUrl = `${window.location.origin}/api/saas/stripe/webhook`;

  const saveMut = useMutation({
    mutationFn: (value: string) =>
      api("/api/admin/secrets", {
        method: "POST",
        body: { key_name: "stripe_webhook_secret", value },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-secrets"] });
      setSaveResult("Webhook secret saved.");
      setUpdating(false);
      setSecret("");
    },
    onError: (e) => {
      console.error(e);
      setSaveResult(`Save failed — ${errorMessage(e)}`);
    },
  });

  function copyWebhookUrl() {
    void navigator.clipboard.writeText(webhookUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<PaymentIcon color="primary" />}
        title="Stripe"
        subheader="Billing & subscription payments"
        action={<StatusChip isSet={isSet} />}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Webhook URL */}
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
            Webhook URL
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "action.hover",
              borderRadius: 1,
              px: 1.5,
              py: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", flex: 1, wordBreak: "break-all", fontSize: 12 }}
            >
              {webhookUrl}
            </Typography>
            <Tooltip title={copied ? "Copied!" : "Copy"}>
              <IconButton size="small" onClick={copyWebhookUrl}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            Register this URL in your Stripe Dashboard under Webhooks.
          </Typography>
        </Box>

        {/* Webhook secret */}
        <SecretField
          label="Webhook Signing Secret"
          value={secret}
          onChange={setSecret}
          isSet={isSet}
          updating={updating}
          onToggleUpdate={() => {
            setUpdating((v) => !v);
            setSecret("");
            setSaveResult(null);
          }}
          placeholder="whsec_…"
        />

        <Box>
          <Button
            size="small"
            variant="contained"
            disabled={saveMut.isPending || (!updating && isSet) || (!isSet && !secret)}
            onClick={() => saveMut.mutate(secret)}
          >
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </Box>

        {saveResult && (
          <Alert severity={saveMut.isError ? "error" : "success"} onClose={() => setSaveResult(null)}>
            {saveResult}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ── SMTP card ─────────────────────────────────────────────────────────────

const SMTP_KEYS = [
  "smtp_host",
  "smtp_port",
  "smtp_username",
  "smtp_password",
  "smtp_from_address",
] as const;
type SmtpKey = (typeof SMTP_KEYS)[number];

function SmtpCard({ secrets }: { secrets: SecretInfo[] }) {
  const qc = useQueryClient();

  const isSet = SMTP_KEYS.some((k) => secrets.find((s) => s.key_name === k)?.is_set);

  const [form, setForm] = useState({
    smtp_host: "",
    smtp_port: "587",
    smtp_username: "",
    smtp_password: "",
    smtp_from_address: "",
    smtp_use_tls: true,
  });
  const [updating, setUpdating] = useState(false);
  const [saveResult, setSaveResult] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  function field(key: SmtpKey) {
    return secrets.find((s) => s.key_name === key);
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      const pairs: { key_name: string; value: string }[] = [
        { key_name: "smtp_host", value: form.smtp_host },
        { key_name: "smtp_port", value: form.smtp_port },
        { key_name: "smtp_username", value: form.smtp_username },
        { key_name: "smtp_password", value: form.smtp_password },
        { key_name: "smtp_from_address", value: form.smtp_from_address },
      ].filter((p) => p.value.trim() !== "");

      for (const pair of pairs) {
        await api("/api/admin/secrets", { method: "POST", body: pair });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-secrets"] });
      setSaveResult("SMTP settings saved.");
      setUpdating(false);
    },
    onError: (e) => {
      console.error(e);
      setSaveResult(`Save failed — ${errorMessage(e)}`);
    },
  });

  const testMut = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; message: string }>("/api/admin/secrets/smtp_host/test", {
        method: "POST",
      }),
    onSuccess: (r) => setTestResult(r),
    onError: (e) => setTestResult({ ok: false, message: errorMessage(e) }),
  });

  const showForm = !isSet || updating;

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<EmailIcon color="primary" />}
        title="SMTP / Email"
        subheader="Outbound transactional email"
        action={<StatusChip isSet={isSet} />}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {isSet && !updating && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {SMTP_KEYS.map((k) => (
              <Chip
                key={k}
                label={field(k)?.label ?? k}
                size="small"
                color={field(k)?.is_set ? "success" : "default"}
                variant="outlined"
              />
            ))}
          </Box>
        )}

        {showForm && (
          <>
            <TextField
              label="Host"
              size="small"
              fullWidth
              value={form.smtp_host}
              onChange={(e) => setForm((p) => ({ ...p, smtp_host: e.target.value }))}
              placeholder="smtp.mailgun.org"
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Port"
                type="number"
                size="small"
                sx={{ width: 100 }}
                value={form.smtp_port}
                onChange={(e) => setForm((p) => ({ ...p, smtp_port: e.target.value }))}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.smtp_use_tls}
                    onChange={(e) => setForm((p) => ({ ...p, smtp_use_tls: e.target.checked }))}
                  />
                }
                label="Use TLS / STARTTLS"
              />
            </Box>
            <TextField
              label="Username"
              size="small"
              fullWidth
              value={form.smtp_username}
              onChange={(e) => setForm((p) => ({ ...p, smtp_username: e.target.value }))}
            />
            <TextField
              label="Password"
              type="password"
              size="small"
              fullWidth
              value={form.smtp_password}
              onChange={(e) => setForm((p) => ({ ...p, smtp_password: e.target.value }))}
            />
            <TextField
              label="From Address"
              size="small"
              fullWidth
              value={form.smtp_from_address}
              onChange={(e) => setForm((p) => ({ ...p, smtp_from_address: e.target.value }))}
              placeholder="no-reply@acme.com"
            />
          </>
        )}

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {isSet && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setUpdating((v) => !v);
                setSaveResult(null);
              }}
            >
              {updating ? "Cancel" : "Update Settings"}
            </Button>
          )}
          {showForm && (
            <Button
              size="small"
              variant="contained"
              disabled={saveMut.isPending}
              onClick={() => saveMut.mutate()}
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            disabled={testMut.isPending || !isSet}
            startIcon={testMut.isPending ? <CircularProgress size={14} /> : undefined}
            onClick={() => {
              setTestResult(null);
              testMut.mutate();
            }}
          >
            Send Test Email
          </Button>
        </Box>

        {testResult && (
          <Alert severity={testResult.ok ? "success" : "error"} onClose={() => setTestResult(null)}>
            {testResult.message}
          </Alert>
        )}
        {saveResult && (
          <Alert severity={saveMut.isError ? "error" : "success"} onClose={() => setSaveResult(null)}>
            {saveResult}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ── AI Models card (Anthropic + OpenAI + Gemini + Mistral + Ollama) ───────

interface TaskModelInfo {
  task: string;
  label: string;
  current_model: string;
  default_model: string;
  is_overridden: boolean;
  estimated_cost_per_call: number;
}

interface AvailableModel {
  id: string;
  label: string;
  input_per_1k: number;
  output_per_1k: number;
}

interface AIModelsResponse {
  tasks: TaskModelInfo[];
  available_models: AvailableModel[];
}

/** Format cost as either fractional cents ($0.0004) or dollars ($0.18). */
function formatCost(usd: number): string {
  if (usd >= 0.01) return `$${usd.toFixed(2)}`;
  if (usd >= 0.001) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(5)}`;
}

// One small inline sub-card for each provider's API key — used inside
// AIModelsCard. Saves to /api/admin/secrets and exposes a Test button.
function ProviderKeySubCard({
  secrets,
  keyName,
  label,
  helper,
  placeholder,
}: {
  secrets: SecretInfo[];
  keyName: string;
  label: string;
  helper: string;
  placeholder?: string;
}) {
  const qc = useQueryClient();
  const isSet = secrets.find((s) => s.key_name === keyName)?.is_set ?? false;
  const [value, setValue] = useState("");
  const [updating, setUpdating] = useState(false);
  const [saveResult, setSaveResult] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const saveMut = useMutation({
    mutationFn: (v: string) =>
      api("/api/admin/secrets", {
        method: "POST",
        body: { key_name: keyName, value: v },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-secrets"] });
      setSaveResult("Saved.");
      setUpdating(false);
      setValue("");
    },
    onError: (e) => setSaveResult(`Save failed — ${errorMessage(e)}`),
  });

  const testMut = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; detail: string }>(`/api/admin/secrets/${keyName}/test`, {
        method: "POST",
      }),
    onSuccess: (r) => setTestResult({ ok: r.ok, message: r.detail }),
    onError: (e) => setTestResult({ ok: false, message: errorMessage(e) }),
  });

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        px: 1.5,
        py: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <StatusChip isSet={isSet} />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
      <SecretField
        label="Value"
        value={value}
        onChange={setValue}
        isSet={isSet}
        updating={updating}
        onToggleUpdate={() => {
          setUpdating((v) => !v);
          setValue("");
          setSaveResult(null);
        }}
        placeholder={placeholder}
      />
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={testMut.isPending || (!isSet && !value)}
          onClick={() => {
            setTestResult(null);
            testMut.mutate();
          }}
        >
          {testMut.isPending ? "Testing…" : "Test"}
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={saveMut.isPending || (!updating && isSet) || (!isSet && !value)}
          onClick={() => saveMut.mutate(value)}
        >
          {saveMut.isPending ? "Saving…" : "Save"}
        </Button>
      </Box>
      {testResult && (
        <Alert severity={testResult.ok ? "success" : "error"} onClose={() => setTestResult(null)}>
          {testResult.message}
        </Alert>
      )}
      {saveResult && (
        <Alert severity={saveMut.isError ? "error" : "success"} onClose={() => setSaveResult(null)}>
          {saveResult}
        </Alert>
      )}
    </Box>
  );
}


function AnthropicCard({ secrets }: { secrets: SecretInfo[] }) {
  const qc = useQueryClient();
  const secretInfo = secrets.find((s) => s.key_name === "anthropic_api_key");
  const isSet = secretInfo?.is_set ?? false;

  const [apiKey, setApiKey] = useState("");
  const [updating, setUpdating] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [keySaveResult, setKeySaveResult] = useState<string | null>(null);
  const [modelsSaveResult, setModelsSaveResult] = useState<string | null>(null);

  // Per-task model registry
  const modelsQ = useQuery({
    queryKey: ["ai-models"],
    queryFn: () => api<AIModelsResponse>("/api/admin/ai-models"),
  });

  // Local edits keyed by task value. Empty/unset → use server's current_model.
  const [pendingModels, setPendingModels] = useState<Record<string, string>>({});

  const testMut = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; message: string }>("/api/admin/secrets/anthropic_api_key/test", {
        method: "POST",
      }),
    onSuccess: (r) => setTestResult(r),
    onError: (e) => setTestResult({ ok: false, message: errorMessage(e) }),
  });

  const saveKeyMut = useMutation({
    mutationFn: async (value: string) => {
      await api("/api/admin/secrets", {
        method: "POST",
        body: { key_name: "anthropic_api_key", value },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-secrets"] });
      setKeySaveResult("API key saved.");
      setUpdating(false);
      setApiKey("");
    },
    onError: (e) => {
      console.error("anthropic_api_key save failed:", e);
      setKeySaveResult(`Save failed — ${errorMessage(e)}`);
    },
  });

  const saveModelsMut = useMutation({
    mutationFn: async () => {
      const overrides = Object.entries(pendingModels).map(([task, model]) => ({
        task,
        model,
      }));
      await api("/api/admin/ai-models", {
        method: "POST",
        body: { overrides },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-models"] });
      setPendingModels({});
      setModelsSaveResult("Per-task model overrides saved.");
    },
    onError: (e) => {
      console.error("ai-models save failed:", e);
      setModelsSaveResult(`Save failed — ${errorMessage(e)}`);
    },
  });

  const resetMut = useMutation({
    mutationFn: async () => {
      // Clear every task by sending empty model strings.
      const tasks = modelsQ.data?.tasks ?? [];
      const overrides = tasks.map((t) => ({ task: t.task, model: "" }));
      await api("/api/admin/ai-models", {
        method: "POST",
        body: { overrides },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-models"] });
      setPendingModels({});
      setModelsSaveResult("Reset to defaults.");
    },
    onError: (e) => setModelsSaveResult(errorMessage(e)),
  });

  const tasks = modelsQ.data?.tasks ?? [];
  const availableModels = modelsQ.data?.available_models ?? [];

  // Pricing lookup so we can recompute cost on-the-fly when the user
  // changes the dropdown but hasn't saved yet. (Uses the same token
  // estimates the backend uses, but indirectly — we just scale the
  // saved cost ratio across input/output by the model's $ per 1k.)
  function previewCostFor(task: TaskModelInfo, model: string): number {
    const cur = availableModels.find((m) => m.id === task.current_model);
    const next = availableModels.find((m) => m.id === model);
    if (!cur || !next) return task.estimated_cost_per_call;
    // Backend cost = ratio of current model's pricing. Approximate by
    // scaling proportionally on input_per_1k (close enough for a hint).
    if (cur.input_per_1k === 0) return task.estimated_cost_per_call;
    const scale = next.input_per_1k / cur.input_per_1k;
    return task.estimated_cost_per_call * scale;
  }

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<SmartToyIcon color="primary" />}
        title="AI Models"
        subheader="Anthropic, OpenAI, Gemini, Mistral, or local Ollama"
        action={<StatusChip isSet={isSet} />}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Providers
        </Typography>

        {/* Anthropic — same flow as before, kept inline so existing
            users don't see a regression. */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            px: 1.5,
            py: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" fontWeight={600}>
              Anthropic (Claude)
            </Typography>
            <StatusChip isSet={isSet} />
          </Box>
          <SecretField
            label="API Key"
            value={apiKey}
            onChange={setApiKey}
            isSet={isSet}
            updating={updating}
            onToggleUpdate={() => {
              setUpdating((v) => !v);
              setApiKey("");
              setKeySaveResult(null);
            }}
            placeholder="sk-ant-…"
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={testMut.isPending || (!isSet && !apiKey)}
              startIcon={testMut.isPending ? <CircularProgress size={14} /> : undefined}
              onClick={() => {
                setTestResult(null);
                testMut.mutate();
              }}
            >
              Test
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={
                saveKeyMut.isPending
                || (!updating && isSet)
                // Disable when the field is empty — the backend requires
                // at least 4 chars. (Bug: previously only checked when no
                // key was set, so Update→Save with empty field 400'd.)
                || apiKey.trim().length < 4
              }
              onClick={() => saveKeyMut.mutate(apiKey.trim())}
            >
              {saveKeyMut.isPending ? "Saving…" : "Save"}
            </Button>
          </Box>
          {testResult && (
            <Alert severity={testResult.ok ? "success" : "error"} onClose={() => setTestResult(null)}>
              {testResult.message}
            </Alert>
          )}
          {keySaveResult && (
            <Alert severity={saveKeyMut.isError ? "error" : "success"} onClose={() => setKeySaveResult(null)}>
              {keySaveResult}
            </Alert>
          )}
        </Box>

        <ProviderKeySubCard
          secrets={secrets}
          keyName="openai_api_key"
          label="OpenAI"
          helper="GPT-5 / GPT-4.1 mini via the LLM router."
          placeholder="sk-…"
        />
        <ProviderKeySubCard
          secrets={secrets}
          keyName="google_api_key"
          label="Google (Gemini)"
          helper="Gemini 2 Pro / Flash via Google AI Studio."
          placeholder="AIza…"
        />
        <ProviderKeySubCard
          secrets={secrets}
          keyName="mistral_api_key"
          label="Mistral"
          helper="Mistral Large via la Plateforme."
          placeholder="…"
        />
        <ProviderKeySubCard
          secrets={secrets}
          keyName="ollama_base_url"
          label="Ollama (local)"
          helper="Local Llama 3 / Mistral. Tool-use limited — chat-only tasks."
          placeholder="http://ollama:11434"
        />

        <Divider />

        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            Per-task Model Selection
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Different tasks have different cost/quality tradeoffs. Estimated costs
            are illustrative — see anthropic.com/pricing for current rates.
          </Typography>
        </Box>

        {modelsQ.isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!modelsQ.isLoading && tasks.length > 0 && (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {/* Header row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1.2fr 0.8fr",
                gap: 1,
                px: 1.5,
                py: 0.75,
                bgcolor: "action.hover",
                fontSize: 11,
                fontWeight: 700,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              <Box>Task</Box>
              <Box>Model</Box>
              <Box sx={{ textAlign: "right" }}>Est. cost/call</Box>
            </Box>

            {tasks.map((t, i) => {
              const selectedModel = pendingModels[t.task] ?? t.current_model;
              const isPending = pendingModels[t.task] !== undefined &&
                                pendingModels[t.task] !== t.current_model;
              const cost = previewCostFor(t, selectedModel);
              return (
                <Box
                  key={t.task}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.4fr 1.2fr 0.8fr",
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    alignItems: "center",
                    borderTop: i === 0 ? "none" : "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box>
                    <Typography variant="body2">{t.label}</Typography>
                    {t.is_overridden && (
                      <Typography variant="caption" color="text.secondary">
                        default: {t.default_model}
                      </Typography>
                    )}
                  </Box>
                  <Select
                    size="small"
                    value={selectedModel}
                    onChange={(e) =>
                      setPendingModels((prev) => ({
                        ...prev,
                        [t.task]: e.target.value,
                      }))
                    }
                    sx={{ minWidth: 0 }}
                  >
                    {availableModels.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "right",
                      fontFamily: "monospace",
                      color: isPending ? "primary.main" : "text.secondary",
                    }}
                  >
                    {formatCost(cost)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={resetMut.isPending || tasks.every((t) => !t.is_overridden)}
            onClick={() => {
              setModelsSaveResult(null);
              resetMut.mutate();
            }}
          >
            Reset to defaults
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={
              saveModelsMut.isPending || Object.keys(pendingModels).length === 0
            }
            onClick={() => {
              setModelsSaveResult(null);
              saveModelsMut.mutate();
            }}
          >
            {saveModelsMut.isPending ? "Saving…" : "Save overrides"}
          </Button>
        </Box>

        {modelsSaveResult && (
          <Alert
            severity={
              saveModelsMut.isError || resetMut.isError ? "error" : "success"
            }
            onClose={() => setModelsSaveResult(null)}
          >
            {modelsSaveResult}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ── Payroll Tax Packs card ─────────────────────────────────────────────────

type BillsAddress = {
  address: string | null;
  configured: boolean;
  available: boolean;
  unavailable_reason: string | null;
};

/**
 * Dedicated bills address — the standalone (no mailbox connector) way for
 * vendor bills to arrive by email. Self-service on purpose: provisioning
 * this used to mean a support ticket.
 *
 * The address carries a random suffix the customer did not choose. That is
 * deliberate and explained in the UI, because otherwise it reads as a bug —
 * every deploy shares one mail domain, so the suffix is what stops two
 * customers claiming one address and crossing their bills.
 */
function BillsAddressCard() {
  const qc = useQueryClient();
  const [prefix, setPrefix] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["ap-intake-email"],
    queryFn: () => api<BillsAddress>("/api/admin/ap-intake-email"),
  });

  const provision = useMutation({
    mutationFn: () =>
      api<BillsAddress>("/api/admin/ap-intake-email", {
        method: "POST",
        body: { prefix },
      }),
    onSuccess: () => {
      setError(null);
      setPrefix("");
      void qc.invalidateQueries({ queryKey: ["ap-intake-email"] });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const release = useMutation({
    mutationFn: () =>
      api<BillsAddress>("/api/admin/ap-intake-email", { method: "DELETE" }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["ap-intake-email"] });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const data = q.data;
  const busy = provision.isPending || release.isPending;

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<EmailIcon color="action" />}
        title="Bills by email"
        subheader="A dedicated address that files vendor bills for you"
        action={<StatusChip isSet={!!data?.configured} />}
      />
      <CardContent>
        <Box sx={{ mb: 1.5 }}>
          <HelpHint
            docPath="bills/email-in.md"
            label="How bills by email works"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Anything emailed to this address is treated as a vendor bill: the
          attachment is read, the vendor matched, and a draft lands in Bills
          Intake for review — the same queue as an uploaded bill.
        </Typography>

        {q.isLoading && <LinearProgress />}

        {data && !data.available && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {data.unavailable_reason}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {data?.configured && data.address && (
          <>
            <Box
              sx={{
                display: "flex", alignItems: "center", gap: 1,
                p: 1.25, mb: 1.5, borderRadius: 1,
                bgcolor: "action.hover", fontFamily: "monospace",
                fontSize: 14, wordBreak: "break-all",
              }}
            >
              <Box sx={{ flex: 1 }}>{data.address}</Box>
              <Tooltip title={copied ? "Copied" : "Copy"}>
                <IconButton
                  size="small"
                  aria-label="Copy bills address"
                  onClick={() => {
                    void navigator.clipboard.writeText(data.address ?? "");
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Give this to vendors, or forward bills to it from your own inbox.
              The characters after your name keep this address unique to you —
              they are not a typo.
            </Typography>
            <Button
              size="small" color="error" variant="outlined" disabled={busy}
              onClick={() => release.mutate()}
            >
              {release.isPending ? "Turning off…" : "Turn off this address"}
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Turning it off stops delivery immediately. Bills already in the
              queue are unaffected.
            </Typography>
          </>
        )}

        {data && !data.configured && data.available && (
          <>
            <TextField
              fullWidth size="small" label="Choose a name for the address"
              placeholder="acme-bills"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              disabled={busy}
              helperText="Lowercase letters, numbers and hyphens. We add a few random characters to keep it unique to you."
              sx={{ mb: 1.5 }}
            />
            <Button
              variant="contained" size="small"
              disabled={busy || prefix.trim().length < 2}
              onClick={() => provision.mutate()}
            >
              {provision.isPending ? "Setting up…" : "Create the address"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PayrollTaxPacksCard() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [downloadResult, setDownloadResult] = useState<string | null>(null);

  const availablePacksQ = useQuery({
    queryKey: ["tax-packs-available"],
    queryFn: () => api<AvailablePack[]>("/api/admin/tax-packs/available"),
  });

  const downloadMut = useMutation({
    mutationFn: (codes: string[]) =>
      api<DownloadPacksResult>("/api/admin/tax-packs/download", {
        method: "POST",
        body: { codes },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["tax-packs-available"] });
      setDownloadResult(r.message);
      setSelected(new Set());
    },
    onError: (e) => setDownloadResult(errorMessage(e)),
  });

  const packs = availablePacksQ.data ?? [];
  const usPacks = packs.filter((p) => p.country === "US");
  const caPacks = packs.filter((p) => p.country === "CA");

  // federal is always in the download (pre-checked, disabled)
  const installedStates = packs.filter(
    (p) => p.is_installed && p.code !== "federal",
  );
  const installedStateCount = installedStates.length;
  const STATE_THRESHOLD = 5;
  const usingSymmetry = installedStateCount > STATE_THRESHOLD;

  function toggleSelect(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function handleDownload() {
    const codes = ["federal", ...Array.from(selected)];
    setDownloadResult(null);
    downloadMut.mutate(codes);
  }

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<WorkIcon color="primary" />}
        title="Payroll Tax Packs"
        subheader="Annual tax tables and withholding rules"
        action={
          installedStateCount > 0 ? (
            <Chip
              label={`${installedStateCount} state${installedStateCount !== 1 ? "s" : ""} installed`}
              color="success"
              size="small"
            />
          ) : (
            <Chip label="No states installed" size="small" />
          )
        }
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Engine routing indicator */}
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {installedStateCount}/{STATE_THRESHOLD} states
            </Typography>
            <Typography
              variant="caption"
              fontWeight={600}
              color={usingSymmetry ? "primary.main" : "success.main"}
            >
              {usingSymmetry ? "Using Symmetry API" : "Using local packs"}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min((installedStateCount / STATE_THRESHOLD) * 100, 100)}
            color={usingSymmetry ? "primary" : "success"}
            sx={{ borderRadius: 1, height: 6 }}
          />
        </Box>

        {/* Installed states chips */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
            Installed States
          </Typography>
          {installedStates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None installed
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {installedStates.map((p) => (
                <Chip key={p.code} label={p.code} size="small" color="success" variant="outlined" />
              ))}
            </Box>
          )}
        </Box>

        <Divider />

        {/* Add states accordion */}
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" fontWeight={600}>
              Add States
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {availablePacksQ.isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <>
                {/* federal — always included, pre-checked */}
                <FormControlLabel
                  control={<Checkbox size="small" checked disabled />}
                  label={
                    <Typography variant="body2">
                      Federal (US) — always included
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  {/* US States */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                      US States
                    </Typography>
                    {usPacks
                      .filter((p) => p.code !== "federal")
                      .map((p) => (
                        <FormControlLabel
                          key={p.code}
                          control={
                            <Checkbox
                              size="small"
                              checked={p.is_installed || selected.has(p.code)}
                              disabled={p.is_installed}
                              onChange={() => toggleSelect(p.code)}
                            />
                          }
                          label={
                            <Typography variant="body2" color={p.is_installed ? "text.disabled" : "text.primary"}>
                              {p.name}
                            </Typography>
                          }
                          sx={{ display: "flex", m: 0, py: 0.25 }}
                        />
                      ))}
                  </Box>

                  {/* Canadian Provinces */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                      Canadian Provinces
                    </Typography>
                    {caPacks.map((p) => (
                      <FormControlLabel
                        key={p.code}
                        control={
                          <Checkbox
                            size="small"
                            checked={p.is_installed || selected.has(p.code)}
                            disabled={p.is_installed}
                            onChange={() => toggleSelect(p.code)}
                          />
                        }
                        label={
                          <Typography variant="body2" color={p.is_installed ? "text.disabled" : "text.primary"}>
                            {p.name}
                          </Typography>
                        }
                        sx={{ display: "flex", m: 0, py: 0.25 }}
                      />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={downloadMut.isPending || selected.size === 0}
                    startIcon={downloadMut.isPending ? <CircularProgress size={14} /> : undefined}
                    onClick={handleDownload}
                  >
                    {downloadMut.isPending
                      ? "Downloading…"
                      : `Download Selected (${selected.size})`}
                  </Button>
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {downloadResult && (
          <Alert
            severity={downloadMut.isError ? "error" : "success"}
            onClose={() => setDownloadResult(null)}
          >
            {downloadResult}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ── Symmetry Payroll Point card ────────────────────────────────────────────

function SymmetryCard({ secrets }: { secrets: SecretInfo[] }) {
  const qc = useQueryClient();
  const secretInfo = secrets.find((s) => s.key_name === "symmetry_api_key");
  const isSet = secretInfo?.is_set ?? false;

  const [apiKey, setApiKey] = useState("");
  const [updating, setUpdating] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [saveResult, setSaveResult] = useState<string | null>(null);

  const testMut = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; detail: string }>("/api/admin/secrets/symmetry_api_key/test", {
        method: "POST",
      }),
    onSuccess: (r) => setTestResult(r),
    onError: (e) => setTestResult({ ok: false, detail: errorMessage(e) }),
  });

  const saveMut = useMutation({
    mutationFn: (value: string) =>
      api("/api/admin/secrets", {
        method: "POST",
        body: { key_name: "symmetry_api_key", value },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-secrets"] });
      setSaveResult("Symmetry API key saved.");
      setUpdating(false);
      setApiKey("");
    },
    onError: (e) => {
      console.error(e);
      setSaveResult(`Save failed — ${errorMessage(e)}`);
    },
  });

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<WorkIcon color="primary" />}
        title="Symmetry Payroll Point"
        subheader="Multi-state payroll tax calculation (6+ states)"
        action={<StatusChip isSet={isSet} />}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SecretField
          label="API Key"
          value={apiKey}
          onChange={setApiKey}
          isSet={isSet}
          updating={updating}
          onToggleUpdate={() => {
            setUpdating((v) => !v);
            setApiKey("");
            setSaveResult(null);
          }}
        />

        <Alert severity="info" sx={{ py: 0.5 }}>
          Automatically used when 6+ states are installed. For 1–5 states, local
          tax packs are used instead.
        </Alert>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={testMut.isPending || (!isSet && !apiKey)}
            startIcon={testMut.isPending ? <CircularProgress size={14} /> : undefined}
            onClick={() => {
              setTestResult(null);
              testMut.mutate();
            }}
          >
            Test Connection
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={saveMut.isPending || (!updating && isSet) || (!isSet && !apiKey)}
            onClick={() => saveMut.mutate(apiKey)}
          >
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </Box>

        {testResult && (
          <Alert
            severity={testResult.ok ? "success" : "error"}
            onClose={() => setTestResult(null)}
          >
            {testResult.detail}
          </Alert>
        )}
        {saveResult && (
          <Alert
            severity={saveMut.isError ? "error" : "success"}
            onClose={() => setSaveResult(null)}
          >
            {saveResult}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ── Bynaus Edge card ───────────────────────────────────────────────────────

function BynausEdgeCard({ secrets }: { secrets: SecretInfo[] }) {
  const qc = useQueryClient();
  const secretInfo = secrets.find((s) => s.key_name === "bynaus_edge_url");
  const isSet = secretInfo?.is_set ?? false;

  const [edgeUrl, setEdgeUrl] = useState("");
  const [updating, setUpdating] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [saveResult, setSaveResult] = useState<string | null>(null);

  const testMut = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; detail: string }>("/api/admin/secrets/bynaus_edge_url/test", {
        method: "POST",
      }),
    onSuccess: (r) => setTestResult(r),
    onError: (e) => setTestResult({ ok: false, detail: errorMessage(e) }),
  });

  const saveMut = useMutation({
    mutationFn: (value: string) =>
      api("/api/admin/secrets", {
        method: "POST",
        body: { key_name: "bynaus_edge_url", value },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-secrets"] });
      setSaveResult("Edge URL saved. Receipt OCR will now use PaddleOCR-VL.");
      setUpdating(false);
      setEdgeUrl("");
    },
    onError: (e) => {
      console.error(e);
      setSaveResult(`Save failed — ${errorMessage(e)}`);
    },
  });

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<DocumentScannerIcon color="primary" />}
        title="Bynaus Edge (local OCR)"
        subheader="PaddleOCR-VL · zero API cost · Apple Silicon"
        action={<StatusChip isSet={isSet} />}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* URL input — not a secret/password, shown in plain text */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            label="Edge base URL"
            size="small"
            fullWidth
            value={isSet && !updating ? "http://localhost:8420" : edgeUrl}
            onChange={(e) => setEdgeUrl(e.target.value)}
            disabled={isSet && !updating}
            placeholder="http://localhost:8420"
            helperText="URL where Bynaus Edge is running on this machine or LAN"
          />
          {isSet && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setUpdating((v) => !v);
                setEdgeUrl("");
                setSaveResult(null);
              }}
              sx={{ whiteSpace: "nowrap", flexShrink: 0, height: 40 }}
            >
              {updating ? "Cancel" : "Update"}
            </Button>
          )}
        </Box>

        <Alert severity="info" sx={{ py: 0.5 }}>
          When configured, receipt and document OCR uses{" "}
          <strong>PaddleOCR-VL running locally</strong> (via mlx_vlm on Apple
          Silicon) instead of Anthropic Vision — reducing AI API cost to $0 per
          scan. Falls back to Anthropic Vision automatically if Edge is offline.
        </Alert>

        <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
            Also enables — Browser automation (RPA)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The Bynaus Chrome extension connects to Edge and lets Ledger Core read data from
            sites you're already logged into — no API keys or app approvals needed.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {[
              { label: "Upwork spend monitoring", detail: "reads your monthly contractor charges automatically — no OAuth API approval required" },
              { label: "Document scanning", detail: "receipts, W-9s, COIs scanned locally at $0/page" },
              { label: "LinkedIn enrichment", detail: "contact & company signals from your existing session" },
            ].map(({ label, detail }) => (
              <Typography key={label} component="li" variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>{label}</strong> — {detail}
              </Typography>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            <strong>Setup:</strong> Start Bynaus Edge on this machine, then install the Chrome extension
            from Edge's dashboard. Log into Upwork (or any target site) in that Chrome browser once —
            Edge handles everything from there.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={testMut.isPending || (!isSet && !edgeUrl)}
            startIcon={testMut.isPending ? <CircularProgress size={14} /> : undefined}
            onClick={() => {
              setTestResult(null);
              testMut.mutate();
            }}
          >
            Test Connection
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={saveMut.isPending || (!updating && isSet) || (!isSet && !edgeUrl)}
            onClick={() => saveMut.mutate(edgeUrl)}
          >
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </Box>

        {testResult && (
          <Alert
            severity={testResult.ok ? "success" : "error"}
            onClose={() => setTestResult(null)}
          >
            {testResult.detail}
          </Alert>
        )}
        {saveResult && (
          <Alert
            severity={saveMut.isError ? "error" : "success"}
            onClose={() => setSaveResult(null)}
          >
            {saveResult}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ── IRIS direct (no Aatrix lock-in) ────────────────────────────────────────

function IrisCard({ secrets }: { secrets: SecretInfo[] }) {
  const qc = useQueryClient();
  const idInfo = secrets.find((s) => s.key_name === "iris_client_id");
  const secretInfo = secrets.find((s) => s.key_name === "iris_client_secret");
  const envInfo = secrets.find((s) => s.key_name === "iris_environment");
  const isSet = (idInfo?.is_set ?? false) && (secretInfo?.is_set ?? false);

  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [env, setEnv] = useState(envInfo?.is_set ? "" : "test");
  const [updating, setUpdating] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [saveResult, setSaveResult] = useState<string | null>(null);

  const testMut = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; detail: string }>("/api/admin/secrets/iris_client_id/test", { method: "POST" }),
    onSuccess: (r) => setTestResult(r),
    onError: (e) => setTestResult({ ok: false, detail: errorMessage(e) }),
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const ops: Promise<unknown>[] = [];
      if (clientId.trim()) ops.push(api("/api/admin/secrets", { method: "POST", body: { key_name: "iris_client_id", value: clientId.trim() } }));
      if (clientSecret.trim()) ops.push(api("/api/admin/secrets", { method: "POST", body: { key_name: "iris_client_secret", value: clientSecret.trim() } }));
      if (env.trim()) ops.push(api("/api/admin/secrets", { method: "POST", body: { key_name: "iris_environment", value: env.trim() } }));
      await Promise.all(ops);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-secrets"] });
      setSaveResult("IRIS settings saved.");
      setUpdating(false);
      setClientId("");
      setClientSecret("");
    },
    onError: (e) => {
      console.error(e);
      setSaveResult(`Save failed — ${errorMessage(e)}`);
    },
  });

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<AccountBalanceIcon color="primary" />}
        title="IRS IRIS A2A (Direct)"
        subheader="Direct W-2 / 1099 e-file · no Aatrix · no per-form fee"
        action={<StatusChip isSet={isSet} />}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
          <TextField
            label="Client ID"
            size="small"
            value={isSet && !updating ? `••••${idInfo?.last_4 ?? ""}` : clientId}
            onChange={(e) => setClientId(e.target.value)}
            disabled={isSet && !updating}
          />
          <TextField
            label="Client Secret"
            size="small"
            type="password"
            value={isSet && !updating ? `••••${secretInfo?.last_4 ?? ""}` : clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            disabled={isSet && !updating}
          />
        </Box>
        <TextField
          select
          label="Environment"
          size="small"
          value={env || (envInfo?.is_set ? "" : "test")}
          onChange={(e) => setEnv(e.target.value)}
          helperText={env === "production"
            ? "⚠ Live filing — submissions transmit to the IRS."
            : "Test mode validates payloads without transmitting."}
        >
          <MenuItem value="test">test (sandbox)</MenuItem>
          <MenuItem value="production">production (LIVE)</MenuItem>
        </TextField>

        <Alert severity="info" sx={{ py: 0.5 }}>
          Apply for IRIS A2A credentials at{" "}
          <a href="https://www.irs.gov/IRIS-A2A" target="_blank" rel="noreferrer">irs.gov/IRIS-A2A</a>{" "}
          — registration takes 2–6 weeks. Until approved, leave in test mode.
        </Alert>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={testMut.isPending}
            startIcon={testMut.isPending ? <CircularProgress size={14} /> : undefined}
            onClick={() => { setTestResult(null); testMut.mutate(); }}
          >
            Test Connection
          </Button>
          {isSet && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => { setUpdating((v) => !v); setClientId(""); setClientSecret(""); setSaveResult(null); }}
            >
              {updating ? "Cancel" : "Update"}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={saveMut.isPending || (!clientId && !clientSecret && !env)}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </Box>

        {testResult && (
          <Alert severity={testResult.ok ? "success" : "error"} onClose={() => setTestResult(null)}>
            {testResult.detail}
          </Alert>
        )}
        {saveResult && (
          <Alert severity={saveMut.isError ? "error" : "success"} onClose={() => setSaveResult(null)}>
            {saveResult}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}


// ── Tax1099.com (third-party) ───────────────────────────────────────────────

function Tax1099Card({ secrets }: { secrets: SecretInfo[] }) {
  const qc = useQueryClient();
  const keyInfo = secrets.find((s) => s.key_name === "tax1099_api_key");
  const envInfo = secrets.find((s) => s.key_name === "tax1099_environment");
  const isSet = keyInfo?.is_set ?? false;

  const [apiKey, setApiKey] = useState("");
  const [env, setEnv] = useState(envInfo?.is_set ? "" : "sandbox");
  const [updating, setUpdating] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [saveResult, setSaveResult] = useState<string | null>(null);

  const testMut = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; detail: string }>("/api/admin/secrets/tax1099_api_key/test", { method: "POST" }),
    onSuccess: (r) => setTestResult(r),
    onError: (e) => setTestResult({ ok: false, detail: errorMessage(e) }),
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const ops: Promise<unknown>[] = [];
      if (apiKey.trim()) ops.push(api("/api/admin/secrets", { method: "POST", body: { key_name: "tax1099_api_key", value: apiKey.trim() } }));
      if (env.trim()) ops.push(api("/api/admin/secrets", { method: "POST", body: { key_name: "tax1099_environment", value: env.trim() } }));
      await Promise.all(ops);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-secrets"] });
      setSaveResult("Tax1099 settings saved.");
      setUpdating(false);
      setApiKey("");
    },
    onError: (e) => {
      console.error(e);
      setSaveResult(`Save failed — ${errorMessage(e)}`);
    },
  });

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<ReceiptLongIcon color="primary" />}
        title="Tax1099.com (Third-Party)"
        subheader="Hands-off W-2 / 1099 / 941 filing · per-form fee"
        action={<StatusChip isSet={isSet} />}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="API Key"
          size="small"
          type="password"
          value={isSet && !updating ? `••••${keyInfo?.last_4 ?? ""}` : apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          disabled={isSet && !updating}
        />
        <TextField
          select
          label="Environment"
          size="small"
          value={env || (envInfo?.is_set ? "" : "sandbox")}
          onChange={(e) => setEnv(e.target.value)}
          helperText={env === "production"
            ? "⚠ Live filing — Tax1099 transmits to the IRS and bills per form."
            : "Sandbox: validation only, no IRS submission, no charge."}
        >
          <MenuItem value="sandbox">sandbox</MenuItem>
          <MenuItem value="production">production (LIVE)</MenuItem>
        </TextField>

        <Alert severity="info" sx={{ py: 0.5 }}>
          Sign up at{" "}
          <a href="https://www.tax1099.com" target="_blank" rel="noreferrer">tax1099.com</a>.
          Pricing is typically $2.90/form for 1099-NEC and W-2. Use this if you don't want
          to apply for IRIS A2A directly.
        </Alert>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={testMut.isPending}
            startIcon={testMut.isPending ? <CircularProgress size={14} /> : undefined}
            onClick={() => { setTestResult(null); testMut.mutate(); }}
          >
            Test Connection
          </Button>
          {isSet && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => { setUpdating((v) => !v); setApiKey(""); setSaveResult(null); }}
            >
              {updating ? "Cancel" : "Update"}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={saveMut.isPending || (!apiKey && !env)}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </Box>

        {testResult && (
          <Alert severity={testResult.ok ? "success" : "error"} onClose={() => setTestResult(null)}>
            {testResult.detail}
          </Alert>
        )}
        {saveResult && (
          <Alert severity={saveMut.isError ? "error" : "success"} onClose={() => setSaveResult(null)}>
            {saveResult}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}


// ── E-file provider selector ────────────────────────────────────────────────

function EFileProviderCard({ secrets }: { secrets: SecretInfo[] }) {
  const qc = useQueryClient();
  const providerInfo = secrets.find((s) => s.key_name === "efile_provider");
  const current = providerInfo?.is_set ? "configured" : "iris (default)";

  const [provider, setProvider] = useState("iris");
  const [saveResult, setSaveResult] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () =>
      api("/api/admin/secrets", {
        method: "POST",
        body: { key_name: "efile_provider", value: provider },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-secrets"] });
      setSaveResult(`Active provider switched to '${provider}'.`);
    },
    onError: (e) => {
      console.error(e);
      setSaveResult(`Save failed — ${errorMessage(e)}`);
    },
  });

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<DescriptionIcon color="primary" />}
        title="E-File Provider"
        subheader={`Active: ${current}`}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Picks which service handles year-end form transmission. Switch any time —
          historical submissions stay in their original provider's environment.
        </Typography>

        <TextField
          select
          label="Provider"
          size="small"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
        >
          <MenuItem value="iris">IRS IRIS — direct (no per-form fee)</MenuItem>
          <MenuItem value="tax1099">Tax1099.com — third-party (per-form fee)</MenuItem>
        </TextField>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? "Saving…" : "Set as Active"}
          </Button>
        </Box>

        {saveResult && (
          <Alert severity={saveMut.isError ? "error" : "success"} onClose={() => setSaveResult(null)}>
            {saveResult}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}


// ── CRM connector card (Salesforce / HubSpot) ──────────────────────────────
//
// Unlike the other cards on this page, the CRM connector has a full OAuth
// lifecycle (external redirect, per-connection config, webhook generation),
// so the heavy setup flow lives on its own focused page at /settings/crm.
// This card is the Integrations-hub entry point: it shows live status and
// routes into that page. Mirrors the index→detail pattern used by Stripe,
// Slack, Linear, etc.

interface CrmConnectionLite {
  provider: "hubspot" | "salesforce";
  status: "active" | "error" | "revoked";
}

function ProviderStatusRow({
  label,
  conn,
}: {
  label: string;
  conn: CrmConnectionLite | undefined;
}) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="body2">{label}</Typography>
      {conn?.status === "active" ? (
        <Chip label="Connected" color="success" size="small" variant="outlined" />
      ) : conn?.status === "error" ? (
        <Chip label="Error" color="error" size="small" variant="outlined" />
      ) : (
        <Chip label="Not connected" size="small" variant="outlined" />
      )}
    </Box>
  );
}

function CrmConnectorCard() {
  const { data: connections = [], isLoading } = useQuery<CrmConnectionLite[]>({
    queryKey: ["crm-connections"],
    queryFn: () => api<CrmConnectionLite[]>("/api/saas/crm/connections"),
  });

  const hubspot = connections.find((c) => c.provider === "hubspot");
  const salesforce = connections.find((c) => c.provider === "salesforce");
  const anyConnected =
    hubspot?.status === "active" || salesforce?.status === "active";

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<SyncIcon color="primary" />}
        title="CRM — Salesforce & HubSpot"
        subheader="Deal-close → subscription. Closed-won deals land in the review queue."
        action={<StatusChip isSet={anyConnected} />}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <ProviderStatusRow label="HubSpot" conn={hubspot} />
            <Divider />
            <ProviderStatusRow label="Salesforce" conn={salesforce} />
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button size="small" variant="contained" href="/settings/crm">
            Manage connections
          </Button>
          <Button size="small" variant="text" href="/saas/crm">
            Open deal queue →
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Concrete Plant existing-system map ────────────────────────────────────

const CONCRETE_PLANT_SOURCE_MAP = [
  {
    name: "Sage 100 and incumbent accounting",
    description: "Confirm the exact product, edition, version, modules, hosting model, entity keys, and extraction path before designing coexistence or migration.",
    path: "Read-only API, ODBC/reporting view, or controlled export",
    capabilities: ["Chart and dimensions", "Customers/vendors", "Open AR/AP", "Trial balance and history"],
  },
  {
    name: "Dispatch and ticketing",
    description: "Bring orders, changes, loads, signed delivery tickets, corrections, and source status into one replay-safe identity chain.",
    path: "Provider API, event feed, or versioned export",
    capabilities: ["Orders", "Loads", "Tickets", "Signatures and corrections"],
  },
  {
    name: "Batch control",
    description: "Link batch targets, actual material consumption, moisture adjustments, mix versions, and tolerance evidence to the correct load.",
    path: "Read-only batch API, database view, or scheduled export",
    capabilities: ["Target vs actual", "Mix version", "Material usage", "Batch evidence"],
  },
  {
    name: "Scales, receiving, and inventory",
    description: "Normalize supplier receipts, scale tickets, transfers, counts, adjustments, waste, reclaim, and location identities without creating a shadow inventory ledger.",
    path: "Scale/ERP feed, EDI, or controlled CSV/XLSX intake",
    capabilities: ["Receipts", "Scale evidence", "Transfers and counts", "Control totals"],
  },
  {
    name: "Fleet, telematics, maintenance, and fuel",
    description: "Match trucks and equipment across location, meters, faults, inspections, maintenance, downtime, fuel cards, payroll, depreciation, and dispatch holds.",
    path: "Read-only APIs and managed transaction feeds",
    capabilities: ["Vehicle identity", "GPS and meters", "Maintenance and holds", "Fuel and operating cost"],
  },
  {
    name: "Quality and laboratory",
    description: "Preserve mix linkage, samples, cylinders, test results, nonconformance, calibration, revisions, and supporting documents as evidence—not self-certification.",
    path: "Lab/QC API, spreadsheet, or document intake",
    capabilities: ["Samples and tests", "Results and revisions", "Calibration", "Corrective action"],
  },
  {
    name: "Microsoft 365, order intake, and telephony",
    description: "Capture controlled orders and evidence from email, spreadsheets, documents, and calls while retaining the human handoff, consent, source, and original record.",
    path: "Microsoft Graph, approved mailbox, file intake, or telephony API",
    capabilities: ["Email and documents", "Spreadsheet intake", "Phone orders", "Human takeover"],
  },
  {
    name: "Plaid banking, payments, tax, and supporting services",
    description: "Bring read-only Plaid bank activity, remittance, payment processors, tax services, and document/AI providers into event-driven reconciliation and continuous-close controls instead of an overnight accounting batch.",
    path: "Plaid plus existing Ledger Core payment, tax, and service connectors",
    capabilities: ["Real-time cash visibility", "Payment status", "Tax evidence", "Continuous close"],
  },
] as const;

function ConcretePlantIntegrationMap() {
  return (
    <Box id="concrete-plant-integration-map" sx={{ scrollMarginTop: 16 }}>
      <CategoryHeader label="Concrete Plant existing-system integration map" />
      <Alert severity="info" sx={{ mb: 2 }}>
        Ledger Core can coexist with the current stack while each source earns trust. We confirm the real provider and system version, start read-only, preserve raw evidence, map identities, prove replay and control totals, and run parallel close before any authority or cutover changes. “Connected” proves access only—not complete mappings, reconciled history, or migration acceptance.
      </Alert>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
        {CONCRETE_PLANT_SOURCE_MAP.map((source) => (
          <Card key={source.name} variant="outlined">
            <CardHeader
              avatar={<SyncIcon color="primary" />}
              title={source.name}
              action={<Chip label="Discovery first" color="info" size="small" variant="outlined" />}
              sx={{ pb: 0, alignItems: "flex-start", "& .MuiCardHeader-action": { mt: 0.5 } }}
            />
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Typography variant="body2" color="text.secondary">{source.description}</Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                {source.capabilities.map((capability) => (
                  <Chip key={capability} label={capability} size="small" variant="outlined" />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Integration path: {source.path}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

// ── Concrete-pumping cloud connectors ─────────────────────────────────────

function OperationsStatusChip({ status }: { status: OperationsIntegration["status"] }) {
  if (status === "connected") return <Chip label="Connected" color="success" size="small" />;
  if (status === "configured") return <Chip label="Ready to test" color="info" size="small" />;
  if (status === "error" || status === "revoked") {
    return <Chip label={status === "error" ? "Needs attention" : "Access revoked"} color="error" size="small" />;
  }
  if (status === "awaiting_provider_access") {
    return <Chip label="Provider access needed" color="warning" size="small" />;
  }
  return <Chip label="Not configured" size="small" />;
}

function OperationsConnectorCard({ integration }: { integration: OperationsIntegration }) {
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>(integration.visible_config);
  const [closeCritical, setCloseCritical] = useState(integration.is_close_critical);
  const [message, setMessage] = useState<{ severity: "success" | "error" | "info"; text: string } | null>(null);

  const saveMut = useMutation({
    mutationFn: () =>
      api<OperationsIntegration>(`/api/operations-integrations/${integration.provider}`, {
        method: "PUT",
        body: { config: values, is_close_critical: closeCritical },
      }),
    onSuccess: (saved) => {
      setValues(saved.visible_config);
      setMessage({ severity: "success", text: "Configuration encrypted and saved. Test the live connection next." });
      void qc.invalidateQueries({ queryKey: ["operations-integrations"] });
    },
    onError: (error) => setMessage({ severity: "error", text: errorMessage(error) }),
  });

  const testMut = useMutation({
    mutationFn: () =>
      api<OperationsConnectionTest>(`/api/operations-integrations/${integration.provider}/test`, {
        method: "POST",
      }),
    onSuccess: (result) => {
      setMessage({ severity: result.ok ? "success" : "error", text: result.detail });
      void qc.invalidateQueries({ queryKey: ["operations-integrations"] });
    },
    onError: (error) => setMessage({ severity: "error", text: errorMessage(error) }),
  });

  const isSaved = integration.status !== "not_configured" && integration.status !== "awaiting_provider_access";

  return (
    <Card variant="outlined" sx={{ display: "flex", flexDirection: "column" }}>
      <CardHeader
        avatar={<SyncIcon color="primary" />}
        title={integration.name}
        subheader={integration.category}
        action={<OperationsStatusChip status={integration.status} />}
        sx={{ pb: 0, alignItems: "flex-start", "& .MuiCardHeader-action": { mt: 0.5 } }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {integration.description}
        </Typography>

        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {integration.capabilities.map((capability) => (
            <Chip key={capability} label={capability} size="small" variant="outlined" />
          ))}
        </Box>

        <Alert severity={integration.requires_partner_access && !isSaved ? "warning" : "info"}>
          {integration.access_note}
        </Alert>

        <Accordion disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={700}>Connection setup</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0 }}>
            {integration.fields.map((field) => {
              const alreadySet = integration.configured_fields.includes(field.key);
              return (
                <TextField
                  key={field.key}
                  label={field.label}
                  type={field.secret ? "password" : "text"}
                  size="small"
                  fullWidth
                  required={field.required && !alreadySet}
                  value={values[field.key] ?? ""}
                  placeholder={alreadySet && field.secret ? "Saved — enter a value only to replace it" : field.placeholder}
                  helperText={field.help_text || (alreadySet && field.secret ? "Credential is saved and never returned to the browser." : " ")}
                  onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))}
                  autoComplete="off"
                />
              );
            })}

            <FormControlLabel
              control={<Checkbox checked={closeCritical} onChange={(event) => setCloseCritical(event.target.checked)} />}
              label={<Typography variant="body2">Treat a failed connection as a period-close blocker</Typography>}
            />

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" variant="contained" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
                {saveMut.isPending ? "Saving…" : isSaved ? "Save changes" : "Save configuration"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!isSaved || testMut.isPending}
                onClick={() => testMut.mutate()}
              >
                {testMut.isPending ? "Testing…" : "Test live connection"}
              </Button>
              <Button size="small" href={integration.docs_url} target="_blank" rel="noreferrer">
                Provider documentation ↗
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {integration.last_success_at && (
          <Typography variant="caption" color="text.secondary">
            Last successful check: {new Date(integration.last_success_at).toLocaleString()}
          </Typography>
        )}
        {integration.last_error && !message && <Alert severity="error">{integration.last_error}</Alert>}
        {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}
      </CardContent>
    </Card>
  );
}

function ConcretePumpingIntegrations() {
  const integrationsQ = useQuery<OperationsIntegration[]>({
    queryKey: ["operations-integrations"],
    queryFn: () => api<OperationsIntegration[]>("/api/operations-integrations"),
  });

  if (integrationsQ.isLoading) return <LinearProgress sx={{ mb: 3 }} />;
  if (integrationsQ.isError) {
    return <Alert severity="error" sx={{ mb: 3 }}>Could not load operations integrations: {errorMessage(integrationsQ.error)}</Alert>;
  }
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
      {(integrationsQ.data ?? []).map((integration) => (
        <OperationsConnectorCard key={integration.provider} integration={integration} />
      ))}
    </Box>
  );
}

// ── Category section header ────────────────────────────────────────────────

function CategoryHeader({ label }: { label: string }) {
  return (
    <Box sx={{ mt: 1, mb: 0.5 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontWeight: 700, letterSpacing: "0.08em", fontSize: 11 }}
      >
        {label}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

function IntegrationsPage() {
  const secretsQ = useQuery({
    queryKey: ["admin-secrets"],
    queryFn: () => api<SecretInfo[]>("/api/admin/secrets"),
  });

  // SaaS pack gate for the CRM card — the same server-confirmed pack list
  // AppShell uses, so a non-SaaS company never sees SaaS integrations.
  const installedQ = useQuery(implementationInstalledQueryOptions());
  const saasEnabled = installedQ.data?.active_packs?.includes("saas") ?? false;

  const secrets = secretsQ.data ?? [];

  if (secretsQ.isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (secretsQ.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load integration status: {String(secretsQ.error)}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Integrations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Manage API keys and configuration for third-party services.
        </Typography>
      </Box>

      <ConcretePlantIntegrationMap />

      <CategoryHeader label="Concrete pumping operations" />
      <Alert severity="info" sx={{ mb: 2 }}>
        Connect dispatch tickets first, then telematics, maintenance, and fuel. A connector is marked Connected only after a live read succeeds; saved credentials alone are not treated as proof of a working feed.
      </Alert>
      <ConcretePumpingIntegrations />

      {/* Tax & Compliance */}
      <CategoryHeader label="Tax & Compliance" />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
        <TaxJarCard secrets={secrets} />
      </Box>

      {/* Billing */}
      <CategoryHeader label="Billing" />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
        <StripeCard secrets={secrets} />
      </Box>

      {/* Sales & CRM — SaaS pack only */}
      {saasEnabled && (
        <>
          <CategoryHeader label="Sales & CRM" />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
            <CrmConnectorCard />
          </Box>
        </>
      )}

      {/* Email */}
      <CategoryHeader label="Email" />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
        <SmtpCard secrets={secrets} />
        <BillsAddressCard />
      </Box>

      {/* AI & Documents */}
      <CategoryHeader label="AI & Documents" />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
        <AnthropicCard secrets={secrets} />
        <BynausEdgeCard secrets={secrets} />
      </Box>

      {/* Payroll */}
      <CategoryHeader label="Payroll" />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
        <PayrollTaxPacksCard />
        <SymmetryCard secrets={secrets} />
      </Box>

      {/* Year-End Filing */}
      <CategoryHeader label="Year-End Filing (W-2 / 1099)" />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
        <EFileProviderCard secrets={secrets} />
        <Box />
        <IrisCard secrets={secrets} />
        <Tax1099Card secrets={secrets} />
      </Box>
    </Box>
  );
}
